import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Icon } from '@rbilabs/universal-components';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { useIntl } from 'react-intl';
import { TouchableOpacity, View } from 'react-native';

import { Loader } from 'components/loader';
import { useIsMobileBreakpoint } from 'hooks/use-media-query';
import { usePrevious } from 'hooks/use-previous';
import { CustomEventNames, EventTypes, logRBIEvent } from 'state/amplitude';
import { useAuthContext } from 'state/auth';
import { ModalAuthScreen, ModalAuthTrigger } from 'state/auth/types';
import { withForterHeaders } from 'state/graphql/links/with-forter-headers';
import { useLocale } from 'state/intl';
import { useScrollViewFullHeight } from 'state/scroll/hooks';
import { getDeviceId } from 'utils/device-id';
import { getConfigValue } from 'utils/environment';
import { isIOS } from 'utils/environment';

import {
  BRAND,
  BRAND_SHORT,
  PYPESTREAM_INTERFACE_ID,
  PYPESTREAM_SCRIPT_ID,
  REDIRECT_TO_LOGIN,
  STAGE,
} from './constants';
import { SupportRouteParams } from './types';
import { bootJS } from './utils';

const shutDownPypeStream = () => window.Pypestream?.shutdown();
const PYPESTREAM_CONTAINER_MARGIN_TOP_DESKTOP = 60;

export const Pypestream = () => {
  useScrollViewFullHeight();
  const bootRef = useRef<() => string>(undefined);
  const isMobile = useIsMobileBreakpoint();
  const params = useLocalSearchParams<SupportRouteParams>();
  const rbiOrderId = params.orderId ?? '';
  const topic = params.topic ?? '';

  const {
    user,
    setOriginLocation,
    isAuthenticated,
    setModalAuthState,
    loading: contextAuthLoading,
  } = useAuthContext();
  const { formatMessage } = useIntl();
  const { language, feCountryCode: isoCountryCode } = useLocale();
  const router = useRouter();
  const { scriptUrl, appId } = getConfigValue({ key: 'pypestream' });
  const [loading, setLoading] = useState(true);
  const [authLoading, setAuthLoading] = useState(contextAuthLoading);
  const [scriptReady, setScriptReady] = useState(false);
  const name = user?.details?.name ?? '';
  const id = `pypestreamWelcome${name ? 'User' : 'Anon'}`;
  const welcomeMessage = formatMessage({ id }, { brand: BRAND, name });
  const loadingMessage = formatMessage({ id: `${BRAND_SHORT}.pypestreamLoadingMessage` });
  const cognitoId = user?.cognitoId ?? '';
  const previousCognitoId = usePrevious(cognitoId) ?? '';
  const cognitoIdHasChanged = cognitoId !== previousCognitoId;
  const forterToken = withForterHeaders?.ttiForterToken ?? '';
  const shouldRequireCognitoId = isAuthenticated && !cognitoId;
  const pypeStreamInterfaceStyles = {
    margin: '0 auto',
    marginTop: isMobile ? 0 : PYPESTREAM_CONTAINER_MARGIN_TOP_DESKTOP,
  };

  bootRef.current = () =>
    bootJS({
      appId,
      cognitoId,
      forterDeviceId: getDeviceId(),
      forterToken,
      language,
      stage: STAGE,
      welcomeMessage,
      isoCountryCode,
      rbiOrderId,
      topic,
    });

  /**
   * Reset Pypestream
   *
   * we need to reset Pypestream when the cognitoId changes
   * because the Pypestream API is not initialized with the new cognitoId
   * and it causes issues.
   */
  const resetPypeStream = useCallback(() => {
    setScriptReady(false);
    setLoading(true);
    setAuthLoading(false);

    // remove the Pypestream API
    if (window?.Pypestream) {
      shutDownPypeStream();
      // Prevents initializing the Pypestream API twice when re-opening the modal
      delete window.Pypestream;
    }

    // Remove Pypestream-related elements
    [
      'pypestream-common-launcher-styles',
      'pypestream-sr-only-wrapper',
      PYPESTREAM_SCRIPT_ID,
      'bootScript',
    ].forEach(id => {
      const element = document.getElementById(id);
      if (element) {
        element.remove();
      }
    });

    bootRef.current = undefined;
  }, [bootRef]);

  const loadScript = useCallback(() => {
    if (window.Pypestream) {
      setScriptReady(true);
      return;
    }

    const pypestreamScript = document.createElement('script');
    pypestreamScript.src = scriptUrl;
    pypestreamScript.id = 'pypestream-script';
    pypestreamScript.async = false;
    pypestreamScript.onload = () => {
      setScriptReady(true);
    };
    document.body.appendChild(pypestreamScript);
  }, [scriptUrl]);

  useEffect(() => {
    if (shouldRequireCognitoId || scriptReady) {
      return;
    }
    loadScript();
  }, [loadScript, shouldRequireCognitoId, scriptReady]);

  // Listen for Pypestream messages
  useEffect(() => {
    if (shouldRequireCognitoId) {
      return;
    }
    // Pypestream calls `window.top.postMessage("REDIRECT_TO_LOGIN", "*")`
    // from within its iframe when the user clicks a "Log In" button in the chat UI
    const listenForMessage = (event: { origin: string; data: string }) => {
      if (
        event.origin?.endsWith('pypestream.com') &&
        event.data === REDIRECT_TO_LOGIN &&
        !cognitoId
      ) {
        // Close current chat before leaving
        shutDownPypeStream();
        setAuthLoading(true);
        setLoading(true);

        setModalAuthState({
          screen: ModalAuthScreen.SIGN_IN,
          trigger: ModalAuthTrigger.Global,
          shouldDisplaySignInSuccessMessage: true,
          shouldWaitLoginForVendor: true,
          onClose: () => {
            setAuthLoading(false);
            window.location.reload();
          },
        });
      }
    };

    window.addEventListener('message', listenForMessage, false);
    return () => {
      window.removeEventListener('message', listenForMessage);
    };
  }, [
    cognitoId,
    setOriginLocation,
    shouldRequireCognitoId,
    isAuthenticated,
    setModalAuthState,
    user,
    resetPypeStream,
  ]);

  /**
   * Boot Pypestream
   *
   * This script is injected into the DOM and it executes the Pypestream boot function
   */
  useEffect(() => {
    if (!scriptReady || !bootRef.current || authLoading || shouldRequireCognitoId) {
      return;
    }
    const innerHTML = bootRef.current();
    const bootScript = document.createElement('script');
    bootScript.type = 'text/javascript';
    bootScript.async = false;
    bootScript.id = 'bootScript';
    bootScript.innerHTML = innerHTML;

    document.body.appendChild(bootScript);
    return () => {
      document.body.removeChild(bootScript);
    };
  }, [scriptReady, authLoading, cognitoId, isAuthenticated, user, shouldRequireCognitoId, bootRef]);

  // Listen for Pypestream onMount event
  // TODO: this event listener piles up when you re-open the Pypestream modal.
  // Find a way to clear out the listeners of Pypestream.
  useEffect(() => {
    if (scriptReady && !shouldRequireCognitoId) {
      window.Pypestream?.onMount(() => {
        setLoading(false);
        setAuthLoading(false);
      });
    }
  }, [scriptReady, cognitoId, isAuthenticated, user, shouldRequireCognitoId]);

  useEffect(() => {
    if (scriptReady && cognitoIdHasChanged) {
      resetPypeStream();
    }
  }, [cognitoIdHasChanged, resetPypeStream, scriptReady]);

  useEffect(() => {
    logRBIEvent({
      name: CustomEventNames.MODAL_APPEARANCE,
      type: EventTypes.Other,
      attributes: {
        Message: 'Pypestream support',
      },
    });
  }, []);

  const handleDismiss = () => {
    // Close current chat before leaving
    shutDownPypeStream();
    router.dismiss();

    // Fix issue on RN WEB on iOS. After rendering Pypestream the app GestureResponders are messed up.
    // Refreshing the page as a temporary fix
    if (isIOS()) {
      setTimeout(() => {
        window.location.reload();
      }, 100);
    }
  };

  return (
    <View style={{ flex: 1, overflow: 'scroll' }}>
      {loading || authLoading ? (
        <Loader copy={loadingMessage} loading />
      ) : (
        // need absolute as pypestream takes up the whole screen
        <TouchableOpacity
          style={{
            position: 'absolute',
            top: 15,
            right: 15,
            zIndex: 1000,
          }}
          onPress={() => handleDismiss()}
          testID="modal-close-button"
          hitSlop={15}
          accessibilityLabel={formatMessage({ id: 'close' })}
        >
          <Icon variant="cancel" />
        </TouchableOpacity>
      )}
      <div id={PYPESTREAM_INTERFACE_ID} style={pypeStreamInterfaceStyles} />
    </View>
  );
};

declare global {
  interface Window {
    Pypestream?: {
      onMount: (callback: () => void) => void;
      shutdown: () => void;
    };
  }
}
